import * as React from 'react'

import { Alert, Box, TextField, Tooltip, InputAdornment } from '@mui/material'
import {
  type GridRowsProp,
  type GridRowModesModel,
  GridRowModes,
  DataGrid,
  type GridColDef,
  type GridEventListener,
  type GridRowId,
  GridRowEditStopReasons,
  type GridSlotProps,
  Toolbar,
  ToolbarButton,
  type GridSlots,
  GridActionsCellItem,
  gridStringOrNumberComparator,
} from '@mui/x-data-grid'

import type { AppState } from 'state/index'
import {
  Clear as ClearIcon,
  SaveOutlined as SaveOutlinedIcon,
  ModeEditOutlineOutlined as ModeEditOutlineOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
} from '@mui/icons-material'
import AddIcon from 'components/add-icon'
import { useLocation } from 'react-router'

import colors from 'common/colors'
import { unitConvert } from '@farmtrx/units'
import { v4 as uuidv4 } from 'uuid'
import {
  createHeaders,
  editHeader,
  deleteHeader,
  getHeaderTypes,
} from 'api/headers'

import { useCallback, useEffect, useState, useRef, type ReactElement } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { type HeaderModel } from 'state/headers'
import { TEST_IDS } from 'test-ids'

const cutWidthFtInStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
  marginLeft: '10px',
}

declare module 'components/farm-settings/headers/index.js' {
  type HeaderErrors = {
    name: string
    cutWidth: string
    widthft: string
    widthin: string
    make: string
    numRows: string
    rowSpacing: string
    units: string
  }

  type HeaderRowData = {
    id: string
    name: string
    headerType: string | null
    cutWidth: number | null
    units: string
    widthin: number | null
    widthft: number | null
    numRows: number | null
    rowSpacing: number | null
  }

  export type HeaderComponentProps = {
    enableHeadersNextButton?: Function
  }
  export function HeadersComponent(
    props: HeaderComponentProps,
  ): ReactElement<any, any>
}

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void
    setRowModesModel: (
      newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
    ) => void
  }
}

const CropHeadersComponentWrapper = (props: HeaderComponentProps) => {
  return (
    <Box
      sx={{
        width: '100%',
        '& .Mui-error': {
          backgroundColor: `rgba(126,10,15, 0.1)`,
          color: '#7e0a0f',
          '& .MuiInputBase-input': {
            borderColor: '#7e0a0f',
          },
        },
      }}
    >
      <CropHeadersComponent {...props} />
    </Box>
  )
}

export default CropHeadersComponentWrapper

const centeredEditCellSx = {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  overflow: 'visible',
  width: '100%',
  padding: '5px',
}

function CropHeadersComponent(
  props: HeaderComponentProps,
): ReactElement<any, any> {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const permissions = useSelector(
    (state) => (state as AppState).account.permissions,
  )
  const headers = useSelector((state) => (state as AppState).headers.collection)

  const headerTypes = useSelector(
    (state) => (state as AppState).headers.headerTypes,
  )

  const userPrefs = useSelector(
    (state) => (state as AppState).account.singleton.prefs,
  )

  const [canEdit, setCanEdit] = useState(false)
  const [rows, setRows] = useState<HeaderRowData[]>([])
  const [rowSelected, setSelectedRow] = useState<string | number>(-1)
  const [addButtonClicked, toggleAddRow] = useState(false)
  const [rowSpacing, setRowSpacing] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<HeaderErrors>({
    name: '',
    cutWidth: '',
    widthft: '',
    widthin: '',
    make: '',
    numRows: '',
    rowSpacing: '',
    units: '',
  })

  const [editObj, setEditObj] = useState({})

  const { pathname } = useLocation()

  const [prefsUnit, setPrefsUnit] = useState({})
  const [disableDeleteItem, setDisableDeleteItem] = useState(false)

  const [touchedFields, setTouchedFields] = useState<
    Record<string, Set<string>>
  >({})
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  )
  const [pendingEditRowId, setPendingEditRowId] = useState<string | null>(null)
  // Names used for sorting rows under edit, so typing doesn't re-sort the row
  // out from under the user before it is saved.
  const frozenSortNamesRef = useRef<Record<GridRowId, string>>({})

  const freezeSortName = useCallback((id: GridRowId, name: string | null) => {
    frozenSortNamesRef.current[id] = name ?? ''
  }, [])

  const unfreezeSortName = useCallback((id: GridRowId) => {
    delete frozenSortNamesRef.current[id]
  }, [])

  useEffect(() => {
    if (!pendingEditRowId) return
    if (!rows.some((row) => row.id === pendingEditRowId)) return

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [pendingEditRowId]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
    }))
    setPendingEditRowId(null)
  }, [rows, pendingEditRowId])

  const markTouched = useCallback((rowId: string, field: string) => {
    setTouchedFields((prev) => {
      const existing = prev[rowId] ? new Set(prev[rowId]) : new Set<string>()
      existing.add(field)
      return { ...prev, [rowId]: existing }
    })
  }, [])

  const CutWidthCell = (cellParams: any) => {
    //if (cellParams.row.headerType === 'row') {
    return getCutWidthDisplay(cellParams.row)
    //}
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('name'),
      width: 230,
      flex: 2,
      editable: true,
      sortComparator: (v1, v2, cellParams1, cellParams2) =>
        gridStringOrNumberComparator(
          frozenSortNamesRef.current[cellParams1.id] ?? v1,
          frozenSortNamesRef.current[cellParams2.id] ?? v2,
          cellParams1,
          cellParams2,
        ),
      renderEditCell: (params) => {
        const isTouched = !!touchedFields[params.id as string]?.has('name')

        const hasError = isTouched && !params.row.name?.trim()
        return (
          <Box sx={centeredEditCellSx}>
            <TextField
              required
              value={params.row.name ?? ''}
              onChange={(e) => {
                const value = e.target.value
                setRows((prevRows) =>
                  prevRows.map((row) =>
                    row.id === params.id ? { ...row, name: value } : row,
                  ),
                )
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'name',
                  value,
                })
              }}
              onBlur={() => markTouched(params.id as string, 'name')}
              error={hasError}
              label={t('enter_header_name')}
              slotProps={{
                htmlInput: {
                  'data-testid': `${TEST_IDS.headerNameInput}-${params.id}`,
                },
              }}
              size="small"
              fullWidth
              autoFocus={params.hasFocus}
            />
          </Box>
        )
      },
    },

    {
      field: 'headerType',
      headerName: t('header_type'),
      width: 100,
      flex: 1,
      editable: true,
      renderEditCell: (params) => {
        return (
          <Box sx={centeredEditCellSx}>
            <TextField
              id="headerType"
              select
              name="headerType"
              variant="outlined"
              placeholder={t('select_header_type')}
              value={params.row.headerType ? params.row.headerType : ''}
              onChange={(e) => {
                handleInputChange(e, params.row)
                const value = e.target.value
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'headerType',
                  value,
                })
                // Sync cleared fields to DataGrid when headerType changes
                if (value !== 'row') {
                  // For pickup/platform, clear numRows and rowSpacing
                  params.api.setEditCellValue({
                    id: params.id,
                    field: 'numRows',
                    value: null,
                  })
                  params.api.setEditCellValue({
                    id: params.id,
                    field: 'rowSpacing',
                    value: null,
                  })
                } else {
                  // For row type, clear cutWidth (it's calculated)
                  params.api.setEditCellValue({
                    id: params.id,
                    field: 'cutWidth',
                    value: null,
                  })
                }
              }}
              slotProps={{
                select: {
                  native: true,
                },
                htmlInput: {
                  'data-testid': `${TEST_IDS.headerTypeSelect}-${params.id}`,
                },
              }}
            >
              {headerTypes ? (
                headerTypes.map((item) => (
                  <option value={item} key={item}>
                    {t(item)}
                  </option>
                ))
              ) : (
                <></>
              )}
            </TextField>
          </Box>
        )
      },
      renderCell: (params) => {
        return params.row.headerType ? t(params.row.headerType) : ''
      },
    },
    {
      field: 'numRows',
      headerName: t('number_of_rows'),
      flex: 1,
      width: 200,
      editable: true,
      type: 'number',
      renderEditCell: (params) => {
        const row = params.row

        if (Object.keys(editObj).length && rowSelected === row.id) {
          if (row.headerType === 'row') {
            return (
              <Box sx={{ ...centeredEditCellSx, pr: 2 }}>
                <TextField
                  required
                  id="numRows"
                  name="numRows"
                  type="number"
                  size="small"
                  fullWidth
                  label={t('enter_number_of_rows')}
                  value={row.numRows ?? ''}
                  disabled={row.id !== rowSelected}
                  slotProps={{
                    htmlInput: {
                      min: 1,
                      step: 1,
                      'data-testid': `${TEST_IDS.headerNumRowsInput}-${params.id}`,
                    },
                  }}
                  onChange={(e) => {
                    handleInputChange(e, row)
                    const numRows =
                      e.target.value === '' ? null : Number(e.target.value)
                    let cutWidth: number | null = null
                    if (numRows && row.rowSpacing) {
                      cutWidth = Number((numRows * row.rowSpacing).toFixed(2))
                    }
                    params.api.setEditCellValue({
                      id: params.id,
                      field: 'numRows',
                      value: e.target.value,
                    })
                    params.api.setEditCellValue({
                      id: params.id,
                      field: 'cutWidth',
                      value: cutWidth,
                    })
                  }}
                  {...(row.id == rowSelected && {
                    ...(errors['numRows'] && {
                      error: true,
                      helperText: errors['numRows'],
                    }),
                  })}
                />
              </Box>
            )
          } else {
            return ''
          }
        } else {
          return row.numRows ?? ''
        }
      },
    },
    {
      field: 'rowSpacing',
      headerName: t('row_spacing'),
      width: 160,
      flex: 1,
      editable: true,
      type: 'number',
      renderCell: (params) => {
        const row = params.row
        const mode = params.cellMode
        if (mode === 'view') {
          if (row.headerType !== 'row' || row.rowSpacing == null) {
            return ''
          }
          if (row.units === 'ftIn') {
            return `${(row.rowSpacing * 39.37).toFixed(1)} ${t('inches')}`
          }
          return `${(row.rowSpacing * 100).toFixed(1)} ${t('cm')}`
        }
      },
      renderEditCell: (params) => {
        const row = params.row
        if (Object.keys(editObj).length) {
          if (row.headerType === 'row') {
            // Check if rowSpacing matches a preset by comparing against stored meter values
            const isPreset70cm =
              row.rowSpacing && Math.abs(row.rowSpacing - 0.7) < 0.001
            const isPreset30in =
              row.rowSpacing && Math.abs(row.rowSpacing - 30 / 39.37) < 0.001
            const showDropdown = isPreset70cm || isPreset30in

            return (
              <Box sx={{ width: '100%' }}>
                {!showDropdown ? (
                  <TextField
                    required
                    label={t('row_spacing')}
                    type="number"
                    name="rowSpacing"
                    size="small"
                    fullWidth
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <span>
                              {getDisplayUnits(row) === 'ftIn'
                                ? t('inches')
                                : t('cm')}
                            </span>
                          </InputAdornment>
                        ),
                      },
                      htmlInput: {
                        min: 1,
                        step: 'any',
                        'data-testid': `${TEST_IDS.headerRowSpacingInput}-${params.id}`,
                      },
                    }}
                    value={getRowSpacingDisplayValue(row)}
                    placeholder={
                      getDisplayUnits(row) === 'ftIn' ? t('inches') : t('cm')
                    }
                    onChange={(e) => {
                      handleInputChange(e, row)
                      const val =
                        e.target.value === '' ? null : Number(e.target.value)
                      const meters =
                        val !== null
                          ? getDisplayUnits(row) === 'ftIn'
                            ? val / 39.37
                            : val / 100
                          : null
                      let cutWidth: number | null = null
                      if (meters && row.numRows) {
                        cutWidth = Number((row.numRows * meters).toFixed(2))
                      }
                      params.api.setEditCellValue({
                        id: params.id,
                        field: 'rowSpacing',
                        value: meters,
                      })
                      params.api.setEditCellValue({
                        id: params.id,
                        field: 'cutWidth',
                        value: cutWidth,
                      })
                    }}
                    {...(errors['rowSpacing'] && {
                      error: true,
                      helperText: errors['rowSpacing'],
                    })}
                  />
                ) : (
                  <TextField
                    select
                    size="small"
                    type="number"
                    fullWidth
                    value={
                      isPreset70cm ? '70cm' : isPreset30in ? '30in' : 'custom'
                    }
                    onChange={(e) => {
                      handleRowSpacingSelectChange(
                        e as React.ChangeEvent<HTMLInputElement>,
                        row,
                      )
                      const val = e.target.value
                      let numericSpacing: number | null = null
                      if (val === '70cm') {
                        numericSpacing = 0.7
                      } else if (val === '30in') {
                        numericSpacing = 30 / 39.37
                      }
                      let cutWidth: number | null = null
                      if (numericSpacing && row.numRows) {
                        cutWidth = Number(
                          (row.numRows * numericSpacing).toFixed(2),
                        )
                      }
                      params.api.setEditCellValue({
                        id: params.id,
                        field: 'rowSpacing',
                        value: numericSpacing,
                      })
                      params.api.setEditCellValue({
                        id: params.id,
                        field: 'cutWidth',
                        value: cutWidth,
                      })
                    }}
                    slotProps={{
                      select: { native: true },
                      htmlInput: {
                        'data-testid': `${TEST_IDS.headerRowSpacingSelect}-${params.id}`,
                      },
                    }}
                  >
                    <option value="70cm">70 cm</option>
                    <option value="30in">30 in</option>
                    <option value="custom">{t('enter_custom_value')}</option>
                  </TextField>
                )}
              </Box>
            )
          }
        } else {
          return displayRowSpacing(row)
        }
        return <Box sx={{ width: '100%' }} />
      },
    },

    {
      field: 'cutWidth',
      headerName: t('cut_width'),
      width: 150,
      flex: 1,
      editable: true,
      type: 'number',
      renderCell: CutWidthCell,
      renderEditCell: (params) => {
        const row = params.row

        // Display when a row is being edited
        if (Object.keys(editObj).length) {
          if (rowSelected === row.id) {
            // Read-only computed cut width for row type
            if (row.headerType === 'row') {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {getCutWidthDisplay(row)}
                </Box>
              )
            } else if (getDisplayUnits(row) === 'm') {
              // Editable cut width in meters
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <TextField
                    type="number"
                    slotProps={{
                      htmlInput: {
                        min: 1,
                        'data-testid': `${TEST_IDS.headerCutWidthInput}-${params.id}`,
                      },
                    }}
                    name="cutWidth"
                    value={row?.cutWidth || ''}
                    required
                    label={t('cut_width')}
                    size="small"
                    disabled={row.id != rowSelected}
                    onChange={(e) => {
                      handleInputChange(e, row)
                      params.api.setEditCellValue({
                        id: params.id,
                        field: 'cutWidth',
                        value:
                          e.target.value === '' ? null : Number(e.target.value),
                      })
                    }}
                    {...(row.id == rowSelected && {
                      ...(errors['cutWidth'] && {
                        error: true,
                      }),
                    })}
                  />
                  {row.headerType === 'pickup' && (
                    <Tooltip title={t('pickup_swather_width_hint')}>
                      <WarningAmberOutlinedIcon
                        fontSize="small"
                        color="warning"
                      />
                    </Tooltip>
                  )}
                </Box>
              )
            } else {
              // Editable cut width in feet and inches
              return (
                <Box style={cutWidthFtInStyle}>
                  {row.headerType === 'pickup' && (
                    <Tooltip title={t('pickup_swather_width_hint')}>
                      <WarningAmberOutlinedIcon
                        fontSize="small"
                        color="warning"
                        style={{ marginRight: 4 }}
                      />
                    </Tooltip>
                  )}
                  <TextField
                    sx={{ width: '14ch' }}
                    type="number"
                    slotProps={{
                      htmlInput: {
                        min: 1,
                        'data-testid': `${TEST_IDS.headerWidthFtInput}-${params.id}`,
                      },
                    }}
                    name="widthft"
                    placeholder={t('in_feet')}
                    value={row?.widthft || ''}
                    required
                    size="small"
                    disabled={row.id != rowSelected}
                    onChange={(e) => {
                      handleInputChange(e, row)
                      const cutWidth = Number(
                        toMetres(e.target.value, row['widthin']),
                      )
                      params.api.setEditCellValue({
                        id: params.id,
                        field: 'cutWidth',
                        value: cutWidth,
                      })
                    }}
                    {...(row.id == rowSelected && {
                      ...(errors['widthft'] && {
                        error: true,
                      }),
                    })}
                  />
                  <TextField
                    sx={{ width: '14ch' }}
                    style={{ marginLeft: 20 }}
                    type="number"
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        max: 12,
                        'data-testid': `${TEST_IDS.headerWidthInInput}-${params.id}`,
                      },
                    }}
                    placeholder={t('in_inches')}
                    name="widthin"
                    value={row?.widthin || ''}
                    required
                    size="small"
                    disabled={row.id != rowSelected}
                    onChange={(e) => {
                      handleInputChange(e, row)
                      const cutWidth = Number(
                        toMetres(row['widthft'], e.target.value),
                      )
                      params.api.setEditCellValue({
                        id: params.id,
                        field: 'cutWidth',
                        value: cutWidth,
                      })
                    }}
                    {...(row.id == rowSelected && {
                      ...(errors['widthin'] && {
                        error: true,
                      }),
                    })}
                  />
                </Box>
              )
            }
          } else if (getDisplayUnits(row) === 'm') {
            // Display for other rows when editing - meters
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {row.cutWidth}
                {row.headerType === 'pickup' && (
                  <Tooltip title={t('pickup_swather_width_hint')}>
                    <WarningAmberOutlinedIcon
                      fontSize="small"
                      color="warning"
                    />
                  </Tooltip>
                )}
              </Box>
            )
          } else {
            // Display for other rows when editing - feet and inches
            return (
              <Box style={cutWidthFtInStyle}>
                {row.headerType === 'pickup' && (
                  <Tooltip title={t('pickup_swather_width_hint')}>
                    <WarningAmberOutlinedIcon
                      fontSize="small"
                      color="warning"
                      style={{ margin: 'auto 4px' }}
                    />
                  </Tooltip>
                )}
                <p>{`${toFeetInches(row?.cutWidth).widthft}'`}</p>
                <p style={{ marginLeft: 20 }}>
                  {`${toFeetInches(row?.cutWidth).widthin}"`}
                </p>
              </Box>
            )
          }
        } else if (getDisplayUnits(row) === 'm') {
          // On load display - meters
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {row.cutWidth}
              {row.headerType === 'pickup' && (
                <Tooltip title={t('pickup_swather_width_hint')}>
                  <WarningAmberOutlinedIcon fontSize="small" color="warning" />
                </Tooltip>
              )}
            </Box>
          )
        } else {
          // On load display - feet and inches
          return (
            <Box style={cutWidthFtInStyle}>
              {row.headerType === 'pickup' && (
                <Tooltip title={t('pickup_swather_width_hint')}>
                  <WarningAmberOutlinedIcon
                    fontSize="small"
                    color="warning"
                    style={{ marginRight: 4 }}
                  />
                </Tooltip>
              )}
              <p>{`${toFeetInches(row?.cutWidth).widthft}'`}</p>
              <p style={{ marginLeft: 20 }}>
                {`${toFeetInches(row?.cutWidth).widthin}"`}
              </p>
            </Box>
          )
        }
      },
    },
    {
      field: 'units',
      headerName: t('units'),
      width: 150,
      flex: 1,
      editable: true,
      renderCell: (params) => {
        const displayUnit = getDisplayUnits(params.row)
        return displayUnit === 'm' ? t('meters') : t('feet_inches')
      },
      renderEditCell: (params) => {
        const row = params.row
        return (
          <Box sx={{ ...centeredEditCellSx, pr: 2 }}>
            <TextField
              id="units"
              select
              name="units"
              size="small"
              fullWidth
              placeholder={t('meters')}
              value={getDisplayUnits(row)}
              //disabled={row.id != rowSelected}
              onChange={(e) => {
                handleInputChange(e, row)
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'units',
                  value: e.target.value,
                })
              }}
              {...(row.id == rowSelected && {
                ...(errors['units'] && {
                  error: true,
                  helperText: errors['units'],
                }),
              })}
              slotProps={{
                select: {
                  native: true,
                },
                htmlInput: {
                  'data-testid': `${TEST_IDS.headerUnitsSelect}-${params.id}`,
                },
              }}
            >
              <option value="ftIn">{t('feet_inches')}</option>
              <option value="m">{t('meters')}</option>
            </TextField>
          </Box>
        )
      },
    },

    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions'),
      width: 120,
      flex: 1,
      cellClassName: 'actions',
      headerClassName: 'actions',
      getActions: (params) => {
        const isInEditMode =
          rowModesModel[params.id]?.mode === GridRowModes.Edit

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key="save"
              icon={
                <SaveOutlinedIcon
                  titleAccess={t('save')}
                  sx={{
                    color: disableButton(params.row)
                      ? colors.grey
                      : colors.brandLight,
                  }}
                />
              }
              label={t('save')}
              data-testid={`${TEST_IDS.headerSaveButton}-${params.id}`}
              disabled={disableButton(params.row)}
              onClick={() => handleSave(params.id)}
            />,

            <GridActionsCellItem
              key="cancel"
              icon={
                <ClearIcon
                  titleAccess={t('cancel')}
                  style={{ color: colors.dangerAccent }}
                />
              }
              label={t('cancel')}
              data-testid={`${TEST_IDS.headerCancelButton}-${params.id}`}
              onClick={() => handleCancel(params.id)}
            />,
          ]
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={
              <ModeEditOutlineOutlinedIcon
                titleAccess={t('edit')}
                style={{ color: colors.brandLight }}
              />
            }
            label={t('edit')}
            data-testid={`${TEST_IDS.headerEditButton}-${params.id}`}
            onClick={() => handleEdit(params.id)}
            disabled={!canEdit}
          />,

          <GridActionsCellItem
            key="delete"
            icon={
              <DeleteOutlineOutlinedIcon
                titleAccess={t('delete')}
                style={{ color: colors.dangerAccent }}
              />
            }
            label={t('delete')}
            data-testid={`${TEST_IDS.headerDeleteButton}-${params.id}`}
            onClick={() => handleDelete(params.id)}
            disabled={!canEdit}
          />,
        ]
      },
    },
  ]

  useEffect(() => {
    //@ts-expect-error
    dispatch(getHeaderTypes())
  }, [])

  useEffect(() => {
    setCanEdit(permissions.includes('farm:write'))
  }, [permissions])

  useEffect(() => {
    const prefsUnit =
      userPrefs?.units?.length_in === 'meters' ||
      userPrefs?.units?.length_in === undefined
        ? 'm'
        : 'ftIn'
    setPrefsUnit(prefsUnit)
  }, [userPrefs])

  useEffect(() => {
    if (
      pathname !== '/farmSetup/headers' &&
      props?.enableHeadersNextButton != undefined
    ) {
      if (headers && headers.length) {
        props?.enableHeadersNextButton(true)
      } else {
        props?.enableHeadersNextButton(false)
      }
    }

    const _rowData: HeaderRowData[] = []
    headers.forEach((item) => {
      let widthin = 0
      let widthft = 0
      if (item.units === 'ftIn') {
        const calculated = toFeetInches(item?.cutWidth)
        widthin = Number(calculated.widthin)
        widthft = Number(calculated.widthft)
      }
      const cutWidth = item.cutWidth
      _rowData.push({
        id: item.id,
        name: item.name,
        headerType: item.headerType,
        cutWidth: cutWidth,
        units: item.units,
        widthin: widthin,
        widthft: widthft,
        numRows: (item as HeaderModel).numRows ?? null,
        rowSpacing: (item as HeaderModel).rowSpacing ?? null,
      })
    })
    setRows(_rowData)
  }, [headers, prefsUnit])

  const toFeetInches = useCallback((val) => {
    if (val != '') {
      const { feet, inches } = unitConvert.convertToFeetInches(val)
      return {
        widthin: inches as number,
        widthft: feet as number,
      }
    } else {
      return {
        widthin: null,
        widthft: null,
      }
    }
  }, [])

  const getRowSpacingDisplayValue = useCallback(
    (row: HeaderRowData): string => {
      if (rowSpacing[row.id] !== undefined) {
        return rowSpacing[row.id]
      }
      return displayRowSpacing(row)
    },
    [rowSpacing],
  )

  const getDisplayUnits = useCallback(
    (row: HeaderRowData): string => {
      return row.units
    },
    [prefsUnit, rowSelected],
  )

  const getCutWidthDisplay = useCallback(
    (row: HeaderRowData): string | number => {
      if (!row.cutWidth) {
        return ''
      }

      if (getDisplayUnits(row) === 'm') {
        return row.cutWidth
      }

      const { widthft, widthin } = toFeetInches(row.cutWidth)
      return `${widthft}' ${widthin}"`
    },
    [getDisplayUnits],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent, updatedRow: HeaderRowData, newValue?: any) => {
      //@ts-ignore
      const { name, value } = e.target
      if (name === 'rowSpacing') {
        setRowSpacing((prev) => ({ ...prev, [updatedRow.id]: value }))
      }
      const { id } = updatedRow
      const newRows = rows.map((row) => {
        if (row.id === id) {
          if (name === 'cutWidth') {
            return { ...row, [name]: Number(value), ['units']: 'm' }
          } else if (name === 'widthin' || name === 'widthft') {
            if (name === 'widthin') {
              return {
                ...row,
                [name]: value,
                ['cutWidth']: Number(toMetres(row['widthft'], value)),
              }
            } else if (name === 'widthft') {
              return {
                ...row,
                [name]: value,
                ['cutWidth']: Number(toMetres(value, row['widthin'])),
              }
            }
          } else if (name === 'units' && value === 'ftIn') {
            const calculated = toFeetInches(row?.cutWidth)
            return {
              ...row,
              widthft: calculated.widthft,
              widthin: calculated.widthin,
              [name]: value,
            }
          } else if (name === 'numRows') {
            const numRows = value === '' ? null : Number(value)
            let cutWidth: number | null = null
            if (numRows && row.rowSpacing) {
              cutWidth = Number((numRows * row.rowSpacing).toFixed(2))
            }
            return { ...row, numRows, cutWidth }
          } else if (name === 'rowSpacing') {
            const rowSpacingInput = value === '' ? null : Number(value)
            let rowSpacing: number | null = null
            let cutWidth: number | null = null
            if (rowSpacingInput !== null && !Number.isNaN(rowSpacingInput)) {
              // Convert from user's preferred unit to metres
              rowSpacing =
                prefsUnit === 'ftIn'
                  ? rowSpacingInput / 39.37
                  : rowSpacingInput / 100
              if (row.numRows) {
                cutWidth = Number((row.numRows * rowSpacing).toFixed(2))
              }
            }
            return { ...row, rowSpacing, cutWidth }
          } else if (name === 'headerType') {
            if (value === 'row') {
              return {
                ...row,
                headerType: value,
                units: 'm',
                cutWidth: null,
                widthft: null,
                widthin: null,
              }
            } else {
              return {
                ...row,
                headerType: value,
                numRows: null,
                rowSpacing: null,
              }
            }
          } else {
            return { ...row, [name]: value }
          }
        }
        validate({ ...row, [name]: value }, { [name]: value })

        return row
      })
      setRows(newRows)
    },
    [rows],
  )

  const validate = (row, data) => {
    const temp: HeaderErrors = { ...errors }
    if ('name' in data) {
      temp.name = data.name ? '' : t('required_field')
    }
    if ('cutWidth' in data) {
      temp.cutWidth =
        data.cutWidth > 0
          ? ''
          : data.cutWidth == 0
            ? 'Cut Width cannot be zero or empty'
            : t('required_field')
    }
    if ('numRows' in data) {
      temp.numRows = data.numRows && data.numRows > 0 ? '' : t('required_field')
    }
    if ('rowSpacing' in data) {
      temp.rowSpacing =
        data.rowSpacing && data.rowSpacing > 0 ? '' : t('required_field')
    }

    //If widthft is updated to zero or empty value but widthin has a non-zero number
    else if (
      'widthft' in data &&
      (data.widthft == 0 || data.widthft === '') &&
      row['widthin'] > 0
    ) {
      temp.widthft = temp.widthin = ''
    }
    //If widthft is updated to zero or empty value and widthin also is empty or zero
    else if (
      ('widthft' in data &&
        (data.widthft == 0 || data.widthft === '') &&
        (row['widthin'] == 0 || row['widthin'] === '')) ||
      ('widthin' in data &&
        (data.widthin == 0 || data.widthin === '') &&
        (row['widthft'] == 0 || row['widthft'] === ''))
    ) {
      temp.widthft = temp.widthin =
        'Width value is required, either in feet or inches.'
    }

    //If widthft is updated to take a non zero value
    else if ('widthft' in data && (data.widthft != 0 || data.widthft !== '')) {
      temp.widthft = temp.widthin = ''
    }

    //If widthin is updated
    else if ('widthin' in data && (data.widthft != 0 || data.widthft !== '')) {
      temp.widthft = temp.widthin = ''
    }
    //end
    setErrors({
      ...temp,
    })
  }

  const enableSelectedRow = useCallback(
    (row) => {
      if (disableDeleteItem) {
        setDisableDeleteItem(false)
      }
      const calculated = toFeetInches(row?.cutWidth)
      let data = { ...row }

      if (row.units === 'ftIn') {
        data = {
          ...data,
          ...{
            widthft: calculated.widthft,
            widthin: calculated.widthin,
          },
        }
      }
      setRows((prevRows) =>
        prevRows.map((currentRow) =>
          currentRow.id === row.id ? { ...currentRow, ...data } : currentRow,
        ),
      )
      setEditObj(data)
      setRowSpacing({})
      toggleAddRow(false)
      setSelectedRow(row.id)
      freezeSortName(row.id, row.name)
      if (row.units === 'ftIn') {
        const index = rows.findIndex((item) => item.id === row.id)
      } else return
    },
    [rows, disableDeleteItem, prefsUnit],
  )

  const toMetres = useCallback((ft, inches) => {
    return (ft * 0.3048 + inches * 0.0254).toFixed(2)
  }, [])

  const handleRowEditStart: GridEventListener<'rowEditStart'> = (
    params,
    event,
  ) => {
    if (!canEdit) {
      event.defaultMuiPrevented = true
      return
    }

    const rowToEdit = rows.find((row) => row.id === params.id)
    if (!rowToEdit) {
      event.defaultMuiPrevented = true
      return
    }

    enableSelectedRow(rowToEdit)
  }

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    // Only Escape (which discards changes) may leave edit mode implicitly.
    // Enter/Tab would otherwise commit the row without the save button's
    // validation, letting incomplete headers reach the API.
    if (params.reason !== GridRowEditStopReasons.escapeKeyDown) {
      event.defaultMuiPrevented = true
      return
    }
    unfreezeSortName(params.id)
  }

  const handleEdit = useCallback(
    (id: GridRowId) => {
      enableSelectedRow(rows.find((row) => row.id === id))
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.Edit },
      }))
    },
    [setRowModesModel, rows],
  )

  const handleSave = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View },
      }))
    },
    [setRowModesModel],
  )

  const handleCancel = useCallback(
    (id: GridRowId) => {
      const isPendingAddRow = addButtonClicked && id === rowSelected
      setTouchedFields((prev) => {
        const { [id as string]: _, ...rest } = prev
        return rest
      })
      if (isPendingAddRow) {
        toggleAddRow(false)
      }
      unfreezeSortName(id)
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }))

      setRows((prevRows) => {
        if (isPendingAddRow) {
          return prevRows.filter((row) => row.id !== id)
        }
        return prevRows
      })
    },
    [addButtonClicked, rowSelected, rows, setRowModesModel],
  )

  const handleDelete = useCallback(
    async (id: GridRowId) => {
      if (headers?.length > 1) {
        //@ts-expect-error
        await dispatch(deleteHeader(id))
        toggleAddRow(false)
        if (rowSelected == id) setEditObj({})
      } else {
        setDisableDeleteItem(true)
      }
    },
    [addButtonClicked, headers, rowSelected, disableDeleteItem],
  )

  const saveData = useCallback(
    async (dataToSave) => {
      const obj = {
        id: dataToSave.id,
        name: dataToSave.name,
        headerType: dataToSave.headerType,
        cutWidth: dataToSave.cutWidth,
        units: dataToSave.units,
        numRows: dataToSave.numRows ?? null,
        rowSpacing: dataToSave.rowSpacing ?? null,
      }

      if (dataToSave.headerType === 'row') {
        obj['cutWidth'] =
          dataToSave.numRows && dataToSave.rowSpacing
            ? Number((dataToSave.numRows * dataToSave.rowSpacing).toFixed(2))
            : null
      } else {
        // For pickup/platform types, always null out numRows and rowSpacing
        obj['numRows'] = null
        obj['rowSpacing'] = null
        if (dataToSave['units'] !== 'm') {
          obj['cutWidth'] = parseFloat(
            toMetres(dataToSave['widthft'], dataToSave['widthin']),
          )
        }
      }

      let result
      if (addButtonClicked) {
        //@ts-expect-error
        result = await dispatch(createHeaders(obj))
      } else {
        //@ts-expect-error
        result = await dispatch(editHeader(obj.id, obj))
      }
      setSelectedRow(-1)
      toggleAddRow(false)
      setEditObj({})
      setRowSpacing({})
      return result
    },
    [headers, rows, editObj],
  )

  const disableButton = useCallback(
    (data) => {
      if (!data?.name) {
        return true
      } else if (data?.headerType === 'row') {
        return (
          !data?.numRows ||
          data.numRows <= 0 ||
          !data?.rowSpacing ||
          data.rowSpacing <= 0
        )
      } else if (
        (data?.units === 'm' && data?.cutWidth === '') ||
        data?.cutWidth <= 0
      ) {
        return true
      } else if (
        data?.units === 'ftIn' &&
        data?.widthft === '' &&
        data?.widthft <= 0 &&
        data?.widthin <= 0
      ) {
        return true
      } else return false
    },
    [rows, headers, errors],
  )

  const handleRowSpacingSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, updatedRow: HeaderRowData) => {
      const value = e.target.value
      if (value === 'custom') {
        setRowSpacing((prev) => ({ ...prev, [updatedRow.id]: '' }))
      } else {
        setRowSpacing((prev) => {
          const { [updatedRow.id]: _, ...rest } = prev
          return rest
        })
      }
      const newRows = rows.map((row) => {
        if (row.id === updatedRow.id) {
          if (value === 'custom') {
            return {
              ...row,
              rowSpacing: null,
              cutWidth: null,
            }
          } else if (value === '70cm') {
            const rowSpacing = 0.7
            const cutWidth =
              row.numRows && rowSpacing
                ? Number((row.numRows * rowSpacing).toFixed(2))
                : null
            return { ...row, rowSpacing, cutWidth }
          } else if (value === '30in') {
            const rowSpacing = 30 / 39.37
            const cutWidth =
              row.numRows && rowSpacing
                ? Number((row.numRows * rowSpacing).toFixed(2))
                : null
            return { ...row, rowSpacing, cutWidth }
          }
        }
        return row
      })
      setRows(newRows)
    },
    [rows],
  )

  const displayRowSpacing = useCallback((row: HeaderRowData): string => {
    if (!row.rowSpacing) return ''
    if (row.units === 'ftIn') {
      const inches = row.rowSpacing * 39.37
      return inches.toFixed(2)
    }
    const cm = row.rowSpacing * 100
    return cm.toFixed(0)
  }, [])

  const EditToolbar = (props: GridSlotProps['toolbar']) => {
    const { setRows: _, setRowModesModel: __, ...other } = props

    const addEmptyRow = useCallback(
      (e) => {
        if (disableDeleteItem) {
          setDisableDeleteItem(false)
        }

        if (addButtonClicked) {
          e.preventDefault()
        } else {
          toggleAddRow(true)
          const id = uuidv4()
          setSelectedRow(id)
          const newRow: HeaderRowData = {
            id,
            name: '',
            units:
              Object.keys(prefsUnit).length > 0 ? (prefsUnit as string) : '',
            cutWidth: null,
            widthft: null,
            widthin: null,
            headerType: 'row',
            numRows: null,
            rowSpacing: prefsUnit === 'ftIn' ? 30 / 39.37 : 0.7,
          }
          freezeSortName(id, newRow.name)
          setRows((prevRows) => [...prevRows, newRow])
          setEditObj(newRow)
          setPendingEditRowId(id)
        }
      },
      [rows, disableDeleteItem],
    )

    return (
      <Toolbar {...other}>
        <Tooltip title={t('add_header')}>
          <ToolbarButton
            data-testid={TEST_IDS.addHeaderButton}
            onClick={addEmptyRow}
          >
            <AddIcon fontSize="small" />
          </ToolbarButton>
        </Tooltip>
      </Toolbar>
    )
  }

  const processRowUpdate = async (newRow: HeaderRowData) => {
    // Single choke point for every commit path: rejecting here keeps the row
    // in edit mode instead of sending an incomplete header to the API.
    if (disableButton(newRow)) {
      throw new Error(
        `Incomplete header, not saved: ${JSON.stringify({
          name: newRow.name,
          headerType: newRow.headerType,
          cutWidth: newRow.cutWidth,
          numRows: newRow.numRows,
          rowSpacing: newRow.rowSpacing,
        })}`,
      )
    }

    const result = await saveData(newRow)
    // Restore the frozen name from before the save to prevent re-sorting with unsaved state
    freezeSortName(newRow.id, result.name)
    unfreezeSortName(newRow.id)

    setRows((prevRows) =>
      prevRows.map((row) => (row.id === result.id ? result : row)),
    )
    return result
  }

  return (
    <>
      <Box
        sx={{
          px: 2,
          py: 2,
          width: '100%',
          maxWidth: 'calc(100vw - 250px)',
          overflowX: 'hidden',
        }}
      >
        {pathname === '/farmSetup/headers' ? (
          <h1>{t('headers')}</h1>
        ) : (
          <h1> {t('select_headers')}</h1>
        )}
        {disableDeleteItem ? (
          <Alert
            severity="error"
            data-testid={TEST_IDS.headerDeleteDisabledAlert}
          >
            {t('delete_item_disable_message', { item: t('header') })}
          </Alert>
        ) : null}
        <Box
          sx={{
            my: 1,
            border: `1px solid ${colors.brand}`,
            borderRadius: '2px',
            width: '100%',
          }}
        >
          <DataGrid
            data-testid={TEST_IDS.headersTable}
            getRowHeight={() => 'auto'}
            sx={{
              flex: 1,
              maxHeight: 'none',

              '& .MuiDataGrid-cell': {
                alignItems: 'center',
                display: 'flex',
                px: 2,
                py: 1.5,
              },

              '& .MuiDataGrid-row': {
                minHeight: 70,
              },

              '& .MuiDataGrid-cell--editing .MuiFormControl-root': {
                my: 0,
                width: '100%',
              },
              '& .MuiInputBase-root': {
                alignItems: 'center',
              },
              '& .MuiTextField-root': {
                width: '100%',
              },
              '& .actions': {
                position: 'sticky',
                right: 0,
                backgroundColor: 'background.paper',
                zIndex: 1,
              },
            }}
            rows={rows}
            columns={columns}
            initialState={{
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            onRowEditStart={handleRowEditStart}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) => {
              console.error(error)
            }}
            showToolbar
            slots={{
              ...(canEdit && { toolbar: EditToolbar as GridSlots['toolbar'] }),
            }}
            slotProps={{
              toolbar: {
                // Only pass standard props to avoid warnings
              },
            }}
          />
        </Box>
      </Box>
    </>
  )
}
